import styled from '@emotion/styled'
import { useCallback, useState } from 'react'

import DefaultModal from '@/components/Modals/DefaultModal'
import ConfirmCancelModalFooter from '@/components/Modals/ConfirmCancelModalFooter'
import { DefaultModalProp } from '@/typings/Modals'
import { useModal } from '@/hooks/useModal'
import { colors } from '@/constants'
import { Headline2, Label, InfoLabel } from '@/components/Typography'
import { ScrollableArea } from '@/shared/components/ScrollbarArea'
import { Flex } from '@/components/Flex'
import { CopyLabel } from '@/components/CopyBox'
import { A } from '@/components/A'

const ServerListItem = styled.div`
  padding: 12px 16px;
  border-bottom: 1px solid ${colors.darkBlue2};
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;

  &:last-child {
    border-bottom: none;
  }
`

const StyledScrollableArea = styled(ScrollableArea)`
  background-color: ${colors.darkBlue3};
  border-radius: 8px;
`

const ServerInfoColumn = styled(Flex)`
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
`

const WarningLabel = styled(Label)`
  color: ${colors.orange};
`

const ITEM_HEIGHT = 64.41
const MAX_VISIBLE_ITEMS = 5

export default function ConfirmServersWithoutPatchModal({
  setOpen,
  ...props
}: DefaultModalProp) {
  const [isLoading, setLoading] = useState(false)
  const modal = useModal('confirmServersWithoutPatch')

  const servers = modal.data?.servers || []
  const shouldScroll = servers.length > MAX_VISIBLE_ITEMS
  const maxHeight =
    servers.length < MAX_VISIBLE_ITEMS
      ? servers.length * ITEM_HEIGHT
      : MAX_VISIBLE_ITEMS * ITEM_HEIGHT

  const onProceed = useCallback(async () => {
    try {
      setLoading(true)
      modal.callbacks?.onConfirm()
      modal.close()
    } catch (error) {
      console.error('Error confirming servers without patch:', error)
    } finally {
      setLoading(false)
    }
  }, [modal])

  return (
    <DefaultModal
      {...props}
      setOpen={setOpen}
      type="confirmServersWithoutPatch"
      modalHeader={<Headline2>Confirm Cluster Creation</Headline2>}
      modalBody={
        <Flex flexDirection="column" width="100%" mt="1rem" gap="0.75rem">
          <Label color="orange">
            Custom Talos Patches have not been created for the following
            servers:
          </Label>
          {servers.length > 0 && (
            <StyledScrollableArea
              maxHeight={maxHeight}
              shouldScroll={shouldScroll}
            >
              {servers.map((server) => (
                <ServerListItem key={server.id}>
                  <ServerInfoColumn>
                    <Label>{server.name}</Label>
                    {server.nodeAddresses && server.nodeAddresses.length > 0 ? (
                      <Flex flexDirection="column" gap="4px">
                        {server.nodeAddresses.map((addr: string, idx: number) => (
                          <CopyLabel
                            key={idx}
                            content={addr}
                            frontChars={6}
                            backChars={5}
                          />
                        ))}
                      </Flex>
                    ) : (
                      <InfoLabel>No Addresses</InfoLabel>
                    )}
                  </ServerInfoColumn>
                  <WarningLabel>Warning</WarningLabel>
                </ServerListItem>
              ))}
            </StyledScrollableArea>
          )}
          <InfoLabel>
            Before creating a cluster, please ensure that the network for these
            servers will be configured automatically in accordance with the
            requirements of the{' '}
            <A
              style={{ textDecoration: 'underline' }}
              href="https://fluence.dev/docs/connect_servers/kubernetes/"
            >
              Official Fluence documentation
            </A>
            .
          </InfoLabel>
        </Flex>
      }
      minHeight="100%"
      modalFooter={ConfirmCancelModalFooter}
      isLoading={isLoading}
      onConfirm={onProceed}
      confirmButtonText="Proceed"
      cancelButtonText="Cancel"
    />
  )
}
